import React, { useState } from 'react';
import { Download, Upload, CheckCircle, AlertCircle, FileJson, Info } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminExportImport = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);

  // ============================================
  // EXPORTAR PRODUCTOS
  // ============================================
  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await fetchWithAuth('/api/admin/products/export');
      
      if (res.ok) {
        const data = await res.json();
        
        // Create downloadable file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `alphatica_products_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`${data.count} productos exportados correctamente`);
      } else {
        toast.error('Error al exportar productos');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setExporting(false);
    }
  };

  // ============================================
  // IMPORTAR PRODUCTOS
  // ============================================
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      setImportResult(null);
      
      // Read file
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Get products array (support both formats)
      const products = data.products || data;
      
      if (!Array.isArray(products)) {
        toast.error('Formato de archivo inválido');
        return;
      }

      // Send to backend
      const res = await fetchWithAuth('/api/admin/products/import', {
        method: 'POST',
        body: { products, overwrite }
      });

      if (res.ok) {
        const result = await res.json();
        setImportResult(result);
        toast.success(result.message);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al importar');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error al procesar el archivo. Verifica que sea un JSON válido.');
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-export-import">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-white">Exportar / Importar Productos</h2>
        <p className="text-gray-400 mt-1">
          Respalda tus productos o migra datos entre entornos
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-8">
        <div className="flex gap-3">
          <Info className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-200">
            <p className="font-medium mb-2">¿Cómo funciona?</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-200/80">
              <li><strong>Exportar:</strong> Descarga todos tus productos en un archivo JSON</li>
              <li><strong>Importar:</strong> Sube un archivo JSON para agregar productos</li>
              <li>Los productos con el mismo código NO se duplican (se omiten o actualizan)</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* EXPORT CARD */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <Download className="text-green-500" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-medium text-white">Exportar Productos</h3>
              <p className="text-sm text-gray-400">Descarga todos tus productos</p>
            </div>
          </div>

          <p className="text-gray-400 text-sm mb-6">
            Genera un archivo JSON con todos los productos de tu catálogo. 
            Incluye imágenes, descripciones, precios y toda la información.
          </p>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            data-testid="export-btn"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Exportando...
              </>
            ) : (
              <>
                <Download size={20} />
                Descargar Productos
              </>
            )}
          </button>
        </div>

        {/* IMPORT CARD */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-500/10 rounded-lg">
              <Upload className="text-amber-500" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-medium text-white">Importar Productos</h3>
              <p className="text-sm text-gray-400">Sube un archivo de productos</p>
            </div>
          </div>

          <p className="text-gray-400 text-sm mb-4">
            Sube un archivo JSON exportado previamente para agregar productos a tu catálogo.
          </p>

          {/* Overwrite option */}
          <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              className="w-5 h-5 accent-amber-500"
            />
            <div>
              <span className="text-sm text-white font-medium">Actualizar existentes</span>
              <p className="text-xs text-gray-400">Si un producto ya existe, actualizar sus datos</p>
            </div>
          </label>

          <label
            className={`w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-600 hover:border-amber-500 text-gray-300 hover:text-amber-500 font-medium rounded-lg transition-colors cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid="import-btn"
          >
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
              disabled={importing}
            />
            {importing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-500 border-t-transparent" />
                Importando...
              </>
            ) : (
              <>
                <FileJson size={20} />
                Seleccionar Archivo JSON
              </>
            )}
          </label>
        </div>
      </div>

      {/* IMPORT RESULT */}
      {importResult && (
        <div className="mt-6 bg-gray-800 rounded-xl p-6">
          <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <CheckCircle className="text-green-500" size={20} />
            Resultado de la Importación
          </h4>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-green-500/10 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-500">{importResult.imported}</p>
              <p className="text-sm text-gray-400">Nuevos</p>
            </div>
            <div className="bg-blue-500/10 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-500">{importResult.updated}</p>
              <p className="text-sm text-gray-400">Actualizados</p>
            </div>
            <div className="bg-gray-500/10 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-400">{importResult.skipped}</p>
              <p className="text-sm text-gray-400">Omitidos</p>
            </div>
          </div>

          {importResult.errors && importResult.errors.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-400 font-medium mb-2 flex items-center gap-2">
                <AlertCircle size={16} />
                Errores ({importResult.errors.length})
              </p>
              <ul className="text-sm text-red-300 space-y-1">
                {importResult.errors.map((err, i) => (
                  <li key={i}>{err.product}: {err.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* INSTRUCTIONS */}
      <div className="mt-8 bg-gray-800/50 rounded-xl p-6">
        <h4 className="text-lg font-medium text-white mb-4">
          Paso a Paso para Migrar a Producción
        </h4>
        <ol className="space-y-3 text-gray-300">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">1</span>
            <span>En esta página (Preview), haz clic en <strong>"Descargar Productos"</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">2</span>
            <span>Se descargará un archivo <code className="bg-gray-700 px-2 py-0.5 rounded">alphatica_products_FECHA.json</code></span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">3</span>
            <span>Ve a tu tienda de <strong>Producción</strong> y accede a <strong>Admin → Exportar/Importar</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">4</span>
            <span>Haz clic en <strong>"Seleccionar Archivo JSON"</strong> y sube el archivo descargado</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-green-500 text-black rounded-full flex items-center justify-center text-sm font-bold">✓</span>
            <span><strong>¡Listo!</strong> Tus productos aparecerán en producción</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default AdminExportImport;
